<template>
  <div class="container-fluid mt-5">
    <div>
      <card
        class="no-border-card"
        body-classes="px-0 pb-1"
        footer-classes="pb-2"
      >
        <template slot="header">
          <div class="row">
            <div class="col-8">
              <h3 class="mb-0 view-header-buttons">
                {{ supplier.company_name }}
                <base-button
                  v-if="$currentUserCan($permissions.PERM_EDIT_SUPPLIERS)"
                  type="primary"
                  icon
                  size="sm"
                  :disabled="loading"
                  @click="editSupplier()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-edit"></i>
                  </span>
                </base-button>

                <base-button
                  v-if="
                    $currentUserCan($permissions.PERM_EDIT_USERS) &&
                    !supplier.active
                  "
                  type="info"
                  icon
                  size="sm"
                  :disabled="loading"
                  @click="activateSupplier()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-power-off"></i>
                    {{ $t("SUPPLIERS.ACTIVATE") }}
                  </span>
                </base-button>

                <base-button
                  v-if="
                    $currentUserCan($permissions.PERM_EDIT_USERS) && supplier.active
                  "
                  type="warning"
                  icon
                  size="sm"
                  :disabled="loading"
                  @click="deactivateSupplier()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-power-off"></i>
                    {{ $t("SUPPLIERS.DEACTIVATE") }}
                  </span>
                </base-button>
                <base-button
                  v-if="$currentUserCan($permissions.PERM_DELETE_SUPPLIERS)"
                  type="danger"
                  icon
                  size="sm"
                  :disabled="loading"
                  @click="deleteSupplier()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </base-button>
              </h3>
            </div>
            <div class="col-4 text-right">
              <base-button
                @click="goBack()"
                type="button"
                class="btn btn-sm btn-primary"
              >
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>

              <notification-subscription
                v-if="$currentUserCan($permissions.PERM_VIEW_SUPPLIERS)"
                :objectType="'suppliers'"
                :objectId="supplier.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
            </div>
          </div>
        </template>
        <div>
          <div
            class="col-12 justify-content-center justify-content-sm-between flex-wrap"
          >
            <tabs
              fill
              class="flex-column flex-md-row"
              tabNavWrapperClasses="nav-wrapper"
              tabNavClasses="nav nav-pills nav-fill"
              value="global"
              :key="renderKey"
            >
              <card shadow>
                <tab-pane title="global" id="1" :active="true">
                  <span slot="title">
                    <i class="ni ni-cloud-upload-96" />
                    {{ $t("COMMON.GLOBAL") }}
                  </span>
                  <supplier-view-global :supplier="supplier" />
                </tab-pane>

                <tab-pane
                  title="contacts"
                  id="2"
                  :active="true"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_CONTACTS)"
                >
                  <span slot="title">
                    <i class="ni ni-circle-08" />
                    {{ $t("COMMON.CONTACTS") }}
                  </span>
                  <supplier-view-contacts :supplier="supplier" />
                </tab-pane>

                <tab-pane
                  title="supplier-products"
                  id="3"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_PRODUCTS)"
                >
                  <span slot="title">
                    <i class="fa fa-cubes" />
                    {{ $t("COMMON.PRODUCTS") }}
                  </span>
                  <supplier-view-supplier-products
                    :supplier="supplier"
                    @supplierUpdated="get"
                  />
                </tab-pane>

                <tab-pane
                  title="supplier-products-last-sync-results"
                  id="4"
                  v-if="
                    $currentUserCan($permissions.PERM_VIEW_ANY_PRODUCTS) &&
                    supplier.syncResults.length > 0
                  "
                >
                  <span slot="title">
                    <i class="fa fa-clipboard-check" />
                    {{ $t("SUPPLIERS.LAST_SYNC_PRODUCTS") }}
                  </span>
                  <supplier-view-last-sync-results
                    :supplier="supplier"
                    :syncResults="supplier.syncResults[0]"
                    @supplierUpdated="get"
                  />
                </tab-pane>

                <tab-pane
                  title="files"
                  id="998"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_FILES)"
                >
                  <span slot="title">
                    <i class="ni ni-folder-17" />
                    {{ $t("COMMON.FILES") }}
                  </span>
                  <list-file-component :object="supplier" />
                </tab-pane>

                <tab-pane
                  title="logs"
                  id="999"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOGS)"
                >
                  <span slot="title">
                    <i class="fa fa-file" />
                    {{ $t("COMMON.LOGS") }}
                  </span>
                  <supplier-view-logs :supplier="supplier" />
                </tab-pane>
              </card>
            </tabs>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import swal from "sweetalert2";
import { cloneDeep } from "lodash";
import { Tabs, TabPane } from "@/components";
import ListFileComponent from "@/components/File/ListFileComponent.vue";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import SupplierViewGlobal from "./partials/SupplierViewGlobal.vue";
import SupplierViewLogs from "./partials/SupplierViewLogs.vue";
import SupplierViewContacts from "./partials/SupplierViewContacts.vue";
import SupplierViewSupplierProducts from "./partials/SupplierViewSupplierProducts.vue";
import defaultSupplier from "./defaultSupplier";
import SupplierViewLastSyncResults from "./partials/SupplierViewLastSyncResults.vue";

export default {
  layout: "DashboardLayout",

  components: {
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
    Tabs,
    TabPane,
    SupplierViewGlobal,
    SupplierViewLogs,
    SupplierViewContacts,
    ListFileComponent,
    NotificationSubscription,
    SupplierViewSupplierProducts,
    SupplierViewLastSyncResults,
  },

  mixins: [],

  data() {
    return {
      loading: false,
      supplier: cloneDeep(defaultSupplier),
      renderKey: 0,
    };

   
  },

  created() {
    this.get();
  },

  methods: {
    async get() {
      try {
        const id = this.$route.params.id;
        await this.$store.dispatch("suppliers/get", id);
        this.supplier = this.$store.getters["suppliers/supplier"];
        this.renderKey++;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async editSupplier(row) {
      this.$router.push({
        name: "Edit Supplier",
        params: { id: this.supplier.id },
      });
    },

    async deleteSupplier() {
      const confirmation = await swal.fire({
        title: this.$t("SUPPLIERS.DELETE_THIS_SUPPLIER"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("suppliers/destroy", this.supplier.id);
          await this.goBack();
          this.$notify({
            type: "success",
            message: this.$t("SUPPLIERS.SUPPLIER_DELETED"),
          });
        }
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    goBack() {
      this.$router.push({ name: "List Suppliers" });
    },

    async activateSupplier() {
      try {
        this.loading = true;
        await this.$store.dispatch("suppliers/activate", this.supplier.id);
        await this.get();
        this.loading = false;
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
    },

    async deactivateSupplier() {
      try {
        this.loading = true;
        await this.$store.dispatch("suppliers/deactivate", this.supplier.id);
        await this.get();
        this.loading = false;
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
    },
  },
};
</script>
